import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { API_URL, api, formatApiErrorDetail } from "@/lib/api";

function JobApplication({ job }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", certifications: "", message: "", contactConsent: false, company_website: "" });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && form.phone.replace(/\D/g, "").length >= 10 && form.contactConsent && resume;

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    if (!valid) {
      const missing = [];
      if (form.name.trim().length < 2) missing.push("your name");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) missing.push("a valid email");
      if (form.phone.replace(/\D/g, "").length < 10) missing.push("a valid phone number");
      if (!resume) missing.push("your resume (PDF, JPG, PNG or WebP)");
      if (!form.contactConsent) missing.push("the contact consent checkbox");
      setError(`Almost there — please add: ${missing.join(", ")}.`);
      return;
    }
    setBusy(true);
    setError("");
    const idem = `job-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      const body = new FormData();
      body.append("payload", JSON.stringify({ ...form, positionId: job.id }));
      body.append("resume", resume, resume.name);
      const res = await fetch(`${API_URL}/job-applications`, { method: "POST", body, credentials: "include", headers: { "Idempotency-Key": idem } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.reference) throw new Error(formatApiErrorDetail(data.detail) || "Submission failed. Please retry.");
      setReference(data.reference);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submission failed. Please retry.");
    } finally {
      setBusy(false);
    }
  }

  if (reference) {
    return (
      <div className="sbb-success" data-testid={`job-success-${job.id}`}>
        <h4>Application received — {reference}</h4>
        <p>Thanks for applying to be our next {job.title}. Your application and resume are saved; we review every application and will reach out about next steps. Keep this reference.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate data-testid={`job-apply-${job.id}`} style={{ marginTop: 14 }}>
      <input type="text" name="company_website" value={form.company_website} onChange={(e) => set("company_website", e.target.value)} style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <div className="sbb-form-grid">
          <label className="sbb-field"><span>Name</span><input required maxLength={160} data-testid={`job-name-${job.id}`} value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
          <label className="sbb-field"><span>Email</span><input required type="email" maxLength={254} data-testid={`job-email-${job.id}`} value={form.email} onChange={(e) => set("email", e.target.value)} /></label>
          <label className="sbb-field"><span>Phone</span><input required type="tel" maxLength={30} inputMode="tel" data-testid={`job-phone-${job.id}`} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></label>
          <label className="sbb-field"><span>Resume (PDF, JPG, PNG or WebP · 15 MB)</span><input required type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid={`job-resume-${job.id}`} onChange={(e) => { setResume(e.target.files?.[0] || null); }} style={{ padding: "8px 0" }} /></label>
        </div>
        <label className="sbb-field"><span>Experience summary</span><textarea rows={3} maxLength={2000} data-testid={`job-experience-${job.id}`} value={form.experience} placeholder="Years, trades, equipment you know — or type 'entry level, ready to learn'." onChange={(e) => set("experience", e.target.value)} /></label>
        <label className="sbb-field"><span>Certifications (EPA 608, OSHA 10, NABCEP, licenses…)</span><input maxLength={300} data-testid={`job-certs-${job.id}`} value={form.certifications} onChange={(e) => set("certifications", e.target.value)} /></label>
        <label className="sbb-check"><input required type="checkbox" checked={form.contactConsent} data-testid={`job-consent-${job.id}`} onChange={(e) => set("contactConsent", e.target.checked)} />I agree to be contacted about this application and have read the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</label>
      </fieldset>
      {error && <p className="sbb-error" role="alert" data-testid={`job-error-${job.id}`} style={{ marginTop: 10 }}>{error}</p>}
      <button className="sbb-primary" type="submit" disabled={busy} data-testid={`job-submit-${job.id}`} style={{ marginTop: 12 }}>{busy ? "Sending…" : `Apply for ${job.title}`}</button>
    </form>
  );
}

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    api.get("/careers-jobs").then((r) => setJobs(r.data.jobs)).catch(() => {});
  }, []);
  return (
    <>
      <Seo
        title="Careers — Solar install jobs across Florida | Solar Black Belt"
        description="Now hiring: solar PV installers, solar electricians, electrical apprentices, metal roofing technicians, HVAC technicians and solar service technicians. Veteran and women owned Florida energy company since 2019 — offices in Fort Myers, Naples and Miami."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">CAREERS · ALL POSITIONS OPEN</span>
          <h1 data-testid="careers-heading">Build the crews that power Florida.</h1>
          <p className="lead" data-testid="careers-company-blurb">
            We're hiring for Solar Black Belt — a Coastal Energy project. The installation provider, Coastal Energy Solutions,
            Inc., is a veteran and women owned Florida energy company operating since 2019, with solar, roofing, electrical and
            HVAC trades under one roof. Work spans the whole state — Southwest Florida, South Florida and Central/Tampa — from
            offices in Fort Myers, Naples and Miami. Licensed crews: Roofing CCC1335978 · Electrical EC13013993 · HVAC
            CAC1816094.
          </p>
          <p className="micro">Every application goes straight to the same inbox as every lead — reviewed by a human, acknowledged with a reference. {jobs.length} positions open now.</p>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          {jobs.map((job, i) => (
            <Reveal key={job.id} delay={Math.min(i * 0.03, 0.15)}>
              <article className="blog-card" style={{ marginBottom: 20, padding: "26px 26px" }} data-testid={`job-card-${job.id}`}>
                <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <h2 style={{ fontSize: 24, margin: 0 }}>{job.title}</h2>
                  <span className="report-tag">{job.type}</span>
                </header>
                <p style={{ marginTop: 10, fontSize: 14, color: "var(--muted)", maxWidth: 760 }}>{job.description}</p>
                <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 10 }}>
                  <div style={{ flex: "1 1 300px" }}>
                    <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>What you'll do</h3>
                    <ul className="benefitlist">{job.responsibilities.map((r) => <li key={r}>{r}</li>)}</ul>
                  </div>
                  <div style={{ flex: "1 1 300px" }}>
                    <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>What we're looking for</h3>
                    <ul className="benefitlist">{job.requirements.map((r) => <li key={r}>{r}</li>)}</ul>
                  </div>
                </div>
                <p className="micro" style={{ marginTop: 10 }}>Compensation: {job.pay}</p>
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 800, color: "var(--teal)" }}>Apply for this position ↓</summary>
                  <JobApplication job={job} />
                </details>
              </article>
            </Reveal>
          ))}
          <p className="micro" data-testid="careers-eoe">
            All qualified applicants receive consideration without regard to race, color, religion, sex, sexual orientation,
            gender identity, national origin, disability or veteran status.
          </p>
        </div>
      </section>
    </>
  );
}
